import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QuantityKgStatus, Report } from "../../types/report.type";
import { api } from "../../api/api";
import {
  Button,
  DatePicker,
  Dropdown,
  Image,
  Menu,
  message,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { API } from "../../config/config";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { DownOutlined } from "@ant-design/icons";
import { UserRole } from "../../types/user.type";
import { useAuth } from "../../contexts/auth.context";
import dayjs from "dayjs";

const { Title } = Typography;
const { RangePicker } = DatePicker;

export default function Reports() {
  const { authState } = useAuth();
  const { itemId, name, type } = useParams();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [quantityKgStatus, setQuantityKgStatus] = useState<
    QuantityKgStatus | undefined
  >(undefined);
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | undefined
  >([dayjs().startOf("month"), dayjs().endOf("month")]);

  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
  };

  const handleExport = () => {
    const exportParams = {
      startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
      endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
    };
    downloadFile(
      `/export/reports/${itemId}`,
      "reports_data.xlsx",
      exportParams
    );
  };

  const handleDownloadReport = (report: Report) => {
    downloadFile(`/export/report/`, `report-${report.date}.pdf`, {
      reportIds: report._id,
    });
  };

  const handleDownloadSelectedReports = () => {
    if (selectedReports.length === 0) {
      message.warning("Пожалуйста, выберите хотя бы один отчет.");
      return;
    }

    const reportIds = selectedReports.join(",");

    downloadFile(`/export/report?reportIds=${reportIds}`, `report.pdf`);
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/user/warehouse-manager/reports/`, {
        params: {
          page,
          limit: pageSize,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
          type,
          warehouseMgrId: itemId,
          quantityKgStatus,
        },
      });
      setReports(response.data.reports);
      setTotal(response.data.total);
    } catch (error) {
      console.log(error);
      message.error("Ошибка получение отчетов.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (reportId: string) => {
    try {
      await api.delete(`/user/warehouse-manager/report/${reportId}`);
      message.success("Отчёт успешно удален.");
      fetchReports();
    } catch (error: any) {
      console.error("Error deleting report", error);
      message.error(error.response.data.error);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [page, pageSize, dateRange, quantityKgStatus]);

  const columns = [
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Фото",
      key: "snapshot",
      dataIndex: "snapshot",
      render: (snapshot: string) => (
        <div>
          <Image
            key={snapshot}
            src={`${API}/${snapshot}`}
            width={50}
            height={50}
            preview={{ src: `${API}/${snapshot}` }}
            alt="Фото"
            loading="lazy"
            style={{
              cursor: "pointer",
              borderRadius: 4,
              border: "1px solid #ddd",
              boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
            }}
          />
        </div>
      ),
    },
    {
      title: "Имя",
      dataIndex: "warehouseMgrName",
      key: "warehouseMgrName",
    },
    {
      title: "Детали",
      key: "details",
      render: (_: any, record: Report) => {
        return (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              maxWidth: "900px",
              margin: 0,
              padding: 0,
            }}
          >
            {record.details.map((detail, index) => (
              <p key={index}>
                {index + 1}.{detail.produceName}, Кол-во:{detail.quantity}, КГ:
                {detail.quantityKg},Отход:{detail.wasteProduced} , Статус:
                {detail.quantityKgStatus}
              </p>
            ))}
          </div>
        );
      },
    },
    {
      title: "",
      key: "actions",
      render: (_: any, report: Report) => {
        const menu = (
          <Menu>
            <Menu.Item onClick={() => handleDownloadReport(report)}>
              Получить отчет
            </Menu.Item>
            {authState.user?.role === UserRole.Admin && (
              <Popconfirm
                title="Вы уверены, что хотите удалить этот отчет ?"
                okText="Да"
                cancelText="Нет"
                onConfirm={() => handleDeleteReport(report._id)}
              >
                <Menu.Item>Удалить</Menu.Item>
              </Popconfirm>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedReports,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedReports(selectedRowKeys as string[]);
    },
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Отчеты {name}</Title>
      <Space style={{ marginBottom: 16 }}>
        <RangePicker
          onChange={handleRangeChange}
          placeholder={["Начало", "Конец"]}
          format="DD.MM.YY"
          value={dateRange}
        />
        <Select
          placeholder="Фильтр по статусу"
          onChange={(value) => setQuantityKgStatus(value)}
          allowClear
          style={{ width: 200 }}
        >
          {Object.values(QuantityKgStatus).map((status) => (
            <Select.Option key={status} value={status}>
              {status}
            </Select.Option>
          ))}
        </Select>
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
        <Button
          type="primary"
          loading={downloadLoading}
          onClick={handleDownloadSelectedReports}
          disabled={selectedReports.length === 0}
        >
          Получить отчет
        </Button>
      </Space>

      <Table
        loading={loading}
        columns={columns}
        dataSource={reports}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
        rowSelection={rowSelection}
      />
    </div>
  );
}
